import { useState, useEffect, useMemo } from "react";
import { useDidUpdate } from "@/hooks/useDidUpdate";
import { reportInr } from "@/services/financialReportsService";
import {
  hasAnyReading,
  receiptCheckService,
  toReading,
  type ReceiptCheckPreview,
} from "@/lib/receiptCheck";
import { todayISO } from "@/lib/utils";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { stockService } from "@/services/stockService";
import { rawMaterialService, supplierService, shedService, breedService } from "@/services/masterDataService";
import { useAuthStore } from "@/store/useAuthStore";
import { chickPurchaseService } from "@/services/chickPurchaseService";
import { handleApiError } from "@/services/api";
import { attachmentService } from "@/services/attachmentService";
import DateRangeFilter, { getDefaultDateRange } from "@/components/common/DateRangeFilter";
import AttachmentUpload from "@/components/form/AttachmentUpload";
import { extractUnusualRate } from "@/lib/unusualRate";
import { isDuplicateInvoice } from "@/lib/duplicateInvoice";
import { extractPaidBillWarning } from "@/lib/paidBillWarning";
import AttachmentViewer from "@/components/common/AttachmentViewer";
import type { RawMaterialStock, RawMaterial, Supplier, Shed, ChickPurchase, Breed, Attachment } from "@/types/models";
import { useIsViewer } from "@/hooks/useRole";
import { useSortable } from "@/hooks/useSortable";
import SortableHeader from "@/components/ui/table/SortableHeader";
import Pagination from "@/components/ui/pagination/Pagination";
import { formatQty, formatRate, displayUnit } from "@/utils/formatQty";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

type ViewTab = "raw_material" | "chicks";
type PurchaseType = "raw_material" | "chicks";

export default function PurchasesPage() {
  const isViewer = useIsViewer();
  // Chick purchases follow the API: owner or supervisor may log and edit one,
  // only the owner may delete. A manager was offered all three and refused.
  const role = useAuthStore((st) => st.user?.role);
  const canWriteChicks = role === "owner" || role === "supervisor";
  const canDeleteChicks = role === "owner";
  const { sortConfig: rmSortConfig, requestSort: rmRequestSort } = useSortable("date", "desc");
  const { sortConfig: chickSortConfig, requestSort: chickRequestSort } = useSortable("date", "desc");
  const [viewTab, setViewTab] = useState<ViewTab>("raw_material");

  // Raw material entries
  const [entries, setEntries] = useState<RawMaterialStock[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Chick purchases
  const [chickPurchases, setChickPurchases] = useState<ChickPurchase[]>([]);
  const [chickTotal, setChickTotal] = useState(0);
  const [chicksLoading, setChicksLoading] = useState(true);

  // Master data
  const [materials, setMaterials] = useState<RawMaterial[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [brooderSheds, setBrooderSheds] = useState<Shed[]>([]);
  const [layerSheds, setLayerSheds] = useState<Shed[]>([]);
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<RawMaterialStock | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RawMaterialStock | null>(null);
  const [editingChick, setEditingChick] = useState<ChickPurchase | null>(null);
  const [deleteChickTarget, setDeleteChickTarget] = useState<ChickPurchase | null>(null);
  // Set when the server asked "this bill has payments — delete anyway?".
  const [paidBillWarning, setPaidBillWarning] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Filters
  const [filterMaterialId, setFilterMaterialId] = useState("");
  const [filterFrom, setFilterFrom] = useState(() => getDefaultDateRange().from);
  const [filterTo, setFilterTo] = useState(() => getDefaultDateRange().to);

  // Form state — shared
  const [purchaseType, setPurchaseType] = useState<PurchaseType>("raw_material");
  const [date, setDate] = useState(todayISO());
  const [supplier, setSupplier] = useState("");
  const [notes, setNotes] = useState("");

  // Form state — raw material
  const [rawMaterialId, setRawMaterialId] = useState("");
  const [qty, setQty] = useState("");
  // The receipt check. THIS is the form that creates a payable (it sends
  // supplier_id); the Stock page sends only a supplier NAME and never does, so
  // a check there could correct the silo but never the bill.
  const [weighbridgeQty, setWeighbridgeQty] = useState("");
  const [moisturePct, setMoisturePct] = useState("");
  const [aflatoxin, setAflatoxin] = useState("");
  const [aflaOverride, setAflaOverride] = useState("");
  // Set when the server asked "is this rate right?"; the checkbox answers it.
  const [unusualRateTypical, setUnusualRateTypical] = useState<number | null>(null);
  const [confirmUnusualRate, setConfirmUnusualRate] = useState(false);
  // Set when the server found this invoice already recorded for the material.
  const [duplicateInvoice, setDuplicateInvoice] = useState(false);
  const [confirmDuplicateInvoice, setConfirmDuplicateInvoice] = useState(false);
  // The preview comes from the SERVER, through the same calculation the save
  // uses. The operator must see the accepted quantity and the rupee deduction
  // BEFORE accepting the lorry — a fat-fingered weighbridge can cut a supplier
  // bill by 90%, and this form used to submit blind.
  const [preview, setPreview] = useState<ReceiptCheckPreview | null>(null);
  const [previewError, setPreviewError] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");

  // Form state — chicks
  const [shedId, setShedId] = useState("");
  const [destinationShedId, setDestinationShedId] = useState("");
  const [birdCount, setBirdCount] = useState("");
  const [costPerBird, setCostPerBird] = useState("");
  const [chickBreedId, setChickBreedId] = useState("");

  // Attachment state
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [pendingAttachmentIds, setPendingAttachmentIds] = useState<number[]>([]);

  const selectedMaterial = useMemo(() => {
    if (!rawMaterialId) return null;
    return materials.find((m) => m.id === parseInt(rawMaterialId)) ?? null;
  }, [materials, rawMaterialId]);

  const materialUnit = selectedMaterial?.unit || "kg";

  // Back-calculate rate per unit from total amount and quantity
  const computedRate = useMemo(() => {
    const q = parseFloat(qty);
    const t = parseFloat(totalAmount);
    if (!q || !t || q <= 0) return null;
    // Convert qty to kg if unit is ton/tonne for rate calculation
    const qtyKg = materialUnit === "ton" || materialUnit === "tonne" ? q * 1000 : q;
    return t / qtyKg;
  }, [qty, totalAmount, materialUnit]);

  // A confirmation answers the question about ONE rate. Change the figures and
  // it no longer applies — the server asks again if the new rate is unusual.
  useEffect(() => {
    setConfirmUnusualRate(false);
    setUnusualRateTypical(null);
  }, [computedRate, rawMaterialId]);

  // Likewise "it is a second delivery" answers it for one invoice, supplier
  // and material.
  useEffect(() => {
    setConfirmDuplicateInvoice(false);
    setDuplicateInvoice(false);
  }, [invoiceNumber, supplier, rawMaterialId]);

  const filteredSuppliers = useMemo(() => {
    if (purchaseType === "chicks") {
      return suppliers.filter((s) => s.details?.supplies_chicks);
    }
    if (!rawMaterialId) return suppliers;
    const matId = parseInt(rawMaterialId);
    return suppliers.filter((s) => s.material_ids?.includes(matId));
  }, [suppliers, rawMaterialId, purchaseType]);

  const loadEntries = async (fromDate?: string, toDate?: string, newOffset?: number) => {
    try {
      const o = newOffset !== undefined ? newOffset : offset;
      const params: Record<string, string | number> = { limit, offset: o, txn_type: "purchase" };
      if (filterMaterialId) params.raw_material_id = parseInt(filterMaterialId);
      const f = fromDate !== undefined ? fromDate : filterFrom;
      const t = toDate !== undefined ? toDate : filterTo;
      if (f) params.from = f;
      if (t) params.to = t;
      params.sort_by = rmSortConfig.key;
      params.sort_order = rmSortConfig.direction;
      const res = await stockService.list(params);
      if (Array.isArray(res)) {
        setEntries(res as unknown as RawMaterialStock[]);
        setTotal((res as unknown as RawMaterialStock[]).length);
      } else {
        const paginated = res as unknown as {
          data: RawMaterialStock[];
          meta: { total: number };
        };
        setEntries(paginated.data ?? []);
        setTotal(paginated.meta?.total ?? 0);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadChickPurchases = async (fromDate?: string, toDate?: string) => {
    try {
      const params: Record<string, string | number> = {};
      const f = fromDate !== undefined ? fromDate : filterFrom;
      const t = toDate !== undefined ? toDate : filterTo;
      if (f) params.from = f;
      if (t) params.to = t;
      params.sort_by = chickSortConfig.key;
      params.sort_order = chickSortConfig.direction;
      const res = await chickPurchaseService.list(params);
      if (Array.isArray(res)) {
        setChickPurchases(res as unknown as ChickPurchase[]);
        setChickTotal((res as unknown as ChickPurchase[]).length);
      } else {
        const paginated = res as unknown as { data: ChickPurchase[]; meta: { total: number } };
        setChickPurchases(paginated.data ?? []);
        setChickTotal(paginated.meta?.total ?? 0);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setChicksLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([
      rawMaterialService.list(),
      supplierService.list(),
      shedService.list(),
      breedService.list(),
    ]).then(([matData, supData, shedData, breedData]) => {
      setMaterials(matData as RawMaterial[]);
      setSuppliers((supData as Supplier[]).filter((s) => s.status === "active"));
      setBrooderSheds((shedData as Shed[]).filter((s) => s.status === "active" && s.shed_type === "brooder"));
      setLayerSheds((shedData as Shed[]).filter((s) => s.status === "active" && s.shed_type === "layer"));
      setBreeds(breedData as Breed[]);
    }).catch(() => {});
    loadEntries();
    loadChickPurchases();
  }, []);

  useEffect(() => {
    setLoading(true);
    loadEntries();
  }, [rmSortConfig]);

  useEffect(() => {
    setChicksLoading(true);
    loadChickPurchases();
  }, [chickSortConfig]);

  // Debounced so a preview is not requested per keystroke. Errors are SHOWN,
  // not swallowed: "we could not check this" and "this load is fine" are
  // different facts.
  useEffect(() => {
    if (purchaseType !== "raw_material" || !rawMaterialId || !qty) {
      setPreview(null);
      setPreviewError("");
      return;
    }
    if (!hasAnyReading(weighbridgeQty, moisturePct, aflatoxin)) {
      setPreview(null);
      setPreviewError("");
      return;
    }
    const isTon = materialUnit === "ton" || materialUnit === "tonne";
    const handle = setTimeout(() => {
      void receiptCheckService
        .preview({
          raw_material_id: parseInt(rawMaterialId),
          qty: isTon ? parseFloat(qty) * 1000 : parseFloat(qty),
          rate_per_unit: computedRate ?? undefined,
          weighbridge_qty: toReading(weighbridgeQty, isTon ? 1000 : 1),
          moisture_pct: toReading(moisturePct, 1),
          aflatoxin: toReading(aflatoxin, 1),
        })
        .then((p) => {
          setPreview(p);
          setPreviewError("");
        })
        .catch((err) => {
          setPreview(null);
          setPreviewError(handleApiError(err));
        });
    }, 350);
    return () => clearTimeout(handle);
  }, [purchaseType, rawMaterialId, qty, weighbridgeQty, moisturePct, aflatoxin, materialUnit, computedRate]);

  const openCreate = () => {
    setEditing(null);
    setEditingChick(null);
    setPurchaseType("raw_material");
    setDate(todayISO());
    setSupplier("");
    setNotes("");
    // Raw material defaults
    setRawMaterialId("");
    setQty("");
    setWeighbridgeQty("");
    setMoisturePct("");
    setAflatoxin("");
    setAflaOverride("");
    setUnusualRateTypical(null);
    setConfirmUnusualRate(false);
    setDuplicateInvoice(false);
    setConfirmDuplicateInvoice(false);
    setTotalAmount("");
    setInvoiceNumber("");
    // Chick defaults
    setShedId("");
    setDestinationShedId("");
    setBirdCount("");
    setCostPerBird("");
    setChickBreedId("");
    setAttachments([]);
    setPendingAttachmentIds([]);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (entry: RawMaterialStock) => {
    setUnusualRateTypical(null);
    setConfirmUnusualRate(false);
    setDuplicateInvoice(false);
    setConfirmDuplicateInvoice(false);
    setEditing(entry);
    setEditingChick(null);
    setPurchaseType("raw_material");
    setRawMaterialId(entry.raw_material_id.toString());
    setDate(new Date(entry.date).toISOString().split("T")[0]);
    const isTon = entry.unit === "ton" || entry.unit === "tonne";
    // THE BILLED quantity, not the accepted one. `qty` on a checked row is
    // what survived the deductions; prefilling that and resubmitting would run
    // the check again against the already-reduced figure and compound it on
    // every edit.
    const billed = entry.bill_qty ?? entry.qty;
    setQty(isTon ? (billed / 1000).toString() : billed.toString());
    // Carried back so a routine edit does not DESTROY the check. The
    // repository assigns every QC column, so an edit that omits them nulls
    // them all — the bill stays cut and every trace of why is gone.
    setWeighbridgeQty(
      entry.weighbridge_qty == null
        ? ""
        : (isTon ? entry.weighbridge_qty / 1000 : entry.weighbridge_qty).toString(),
    );
    setMoisturePct(entry.moisture_pct?.toString() ?? "");
    setAflatoxin(entry.aflatoxin?.toString() ?? "");
    setAflaOverride(entry.aflatoxin_override_note ?? "");
    // Back-calculate total amount from the BILLED quantity and rate: the rate
    // is per unit and the invoice is for what was billed.
    if (entry.rate_per_unit != null) {
      setTotalAmount((billed * entry.rate_per_unit).toFixed(2));
    } else {
      setTotalAmount("");
    }
    setInvoiceNumber(entry.invoice_number || "");
    setSupplier(entry.supplier_id ? entry.supplier_id.toString() : "");
    setNotes(entry.notes || "");
    setPendingAttachmentIds([]);
    setError("");
    setModalOpen(true);
    // Load existing attachments for this entry
    attachmentService
      .listBySource("raw_material_stock", entry.id)
      .then((data) => setAttachments(data ?? []))
      .catch(() => setAttachments([]));
  };

  const openEditChick = (cp: ChickPurchase) => {
    setEditing(null);
    setEditingChick(cp);
    setPurchaseType("chicks");
    setDate(new Date(cp.date).toISOString().split("T")[0]);
    setBirdCount(cp.bird_count.toString());
    setCostPerBird(cp.cost_per_bird != null ? cp.cost_per_bird.toString() : "");
    setSupplier(cp.supplier_id ? cp.supplier_id.toString() : "");
    setNotes(cp.notes || "");
    // Shed context is read-only in edit mode (backend ignores shed changes),
    // so we don't need select IDs — see the disabled fields in the form.
    setShedId("");
    setDestinationShedId("");
    setChickBreedId("");
    setAttachments([]);
    setPendingAttachmentIds([]);
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (purchaseType === "chicks") {
        if (editingChick) {
          await chickPurchaseService.update(editingChick.id, {
            bird_count: parseInt(birdCount),
            cost_per_bird: costPerBird ? parseFloat(costPerBird) : undefined,
            supplier_id: supplier ? parseInt(supplier) : undefined,
            date,
            notes: notes || undefined,
          });
        } else {
          await chickPurchaseService.create({
            shed_id: parseInt(shedId),
            destination_shed_id: parseInt(destinationShedId),
            date,
            bird_count: parseInt(birdCount),
            cost_per_bird: costPerBird ? parseFloat(costPerBird) : undefined,
            supplier_id: supplier ? parseInt(supplier) : undefined,
            breed_id: chickBreedId ? parseInt(chickBreedId) : undefined,
            notes: notes || undefined,
            attachment_ids: pendingAttachmentIds.length > 0 ? pendingAttachmentIds : undefined,
          });
        }
        setModalOpen(false);
        setChicksLoading(true);
        await loadChickPurchases();
        setViewTab("chicks");
      } else {
        const isTonneUnit = materialUnit === "ton" || materialUnit === "tonne";
        const payload = {
          raw_material_id: parseInt(rawMaterialId),
          txn_type: "purchase" as const,
          date,
          qty: materialUnit === "ton" || materialUnit === "tonne" ? parseFloat(qty) * 1000 : parseFloat(qty),
          rate_per_unit: computedRate ?? undefined,
          supplier_id: supplier ? parseInt(supplier) : undefined,
          supplier: supplier ? filteredSuppliers.find(s => s.id === parseInt(supplier))?.name : undefined,
          invoice_number: invoiceNumber || undefined,
          notes: notes || undefined,
          attachment_ids: pendingAttachmentIds.length > 0 ? pendingAttachmentIds : undefined,
          // The weighbridge reading converts EXACTLY as the quantity does — it
          // is compared against the billed figure, and a tonne against a
          // kilogram reports a 99.9% shortage on every load.
          weighbridge_qty: toReading(weighbridgeQty, isTonneUnit ? 1000 : 1),
          moisture_pct: toReading(moisturePct, 1),
          aflatoxin: toReading(aflatoxin, 1),
          aflatoxin_override_note: aflaOverride.trim() === "" ? undefined : aflaOverride.trim(),
          confirm_unusual_rate: confirmUnusualRate || undefined,
          confirm_duplicate_invoice: confirmDuplicateInvoice || undefined,
        };

        if (editing) {
          await stockService.update(editing.id, payload);
        } else {
          await stockService.create(payload);
        }
        setModalOpen(false);
        setLoading(true);
        await loadEntries();
      }
    } catch (err) {
      const typical = extractUnusualRate(err);
      if (typical !== null) setUnusualRateTypical(typical);
      if (isDuplicateInvoice(err)) setDuplicateInvoice(true);
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError("");
    try {
      // A second press after the warning is the owner saying yes.
      await stockService.delete(deleteTarget.id, paidBillWarning !== null);
      setDeleteTarget(null);
      setPaidBillWarning(null);
      setLoading(true);
      await loadEntries();
    } catch (err) {
      const warning = extractPaidBillWarning(err);
      if (warning) setPaidBillWarning(warning);
      else setError(handleApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteChick = async () => {
    if (!deleteChickTarget) return;
    setDeleting(true);
    setError("");
    try {
      await chickPurchaseService.delete(deleteChickTarget.id, paidBillWarning !== null);
      setDeleteChickTarget(null);
      setPaidBillWarning(null);
      setChicksLoading(true);
      await loadChickPurchases();
    } catch (err) {
      const warning = extractPaidBillWarning(err);
      if (warning) setPaidBillWarning(warning);
      else setError(handleApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setEditingChick(null);
  };

  const handleDateRangeApply = (from: string, to: string) => {
    setFilterFrom(from);
    setFilterTo(to);
    setOffset(0);
    setLoading(true);
    loadEntries(from, to, 0);
    setChicksLoading(true);
    loadChickPurchases(from, to);
  };

  // A dropdown applies the moment it changes; there is no separate Filter button.
  // The material filter applies to raw-material purchases only; chick purchases ignore it.
  useDidUpdate(() => {
    setOffset(0);
    setLoading(true);
    loadEntries(undefined, undefined, 0);
  }, [filterMaterialId]);

  const handlePrev = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
    loadEntries(undefined, undefined, newOffset);
  };

  const handleNext = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    loadEntries(undefined, undefined, newOffset);
  };

  const isRawMaterialFormValid = rawMaterialId && qty && supplier;
  const isChickFormValid = editingChick
    ? birdCount && supplier
    : shedId && destinationShedId && birdCount && supplier;

  return (
    <>
      <PageMeta
        title="Purchases | Poultry Book"
        description="Purchase logging"
      />
      <PageBreadcrumb pageTitle="Purchases" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Purchases
          </h3>
          {!isViewer && (
            <Button size="sm" onClick={openCreate}>
              Log Purchase
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-gray-200 px-5 dark:border-gray-800 sm:px-6">
          <button
            onClick={() => setViewTab("raw_material")}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              viewTab === "raw_material"
                ? "border-brand-500 text-brand-600 dark:text-brand-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            Raw Materials
            {total > 0 && viewTab === "raw_material" && (
              <span className="ml-1.5 text-xs font-normal text-gray-400">({total})</span>
            )}
          </button>
          <button
            onClick={() => setViewTab("chicks")}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              viewTab === "chicks"
                ? "border-brand-500 text-brand-600 dark:text-brand-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            Chicks
            {chickTotal > 0 && viewTab === "chicks" && (
              <span className="ml-1.5 text-xs font-normal text-gray-400">({chickTotal})</span>
            )}
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 sm:px-6">
          {viewTab === "raw_material" && (
            <select
              value={filterMaterialId}
              onChange={(e) => setFilterMaterialId(e.target.value)}
              className={`${selectClass} max-w-[200px]`}
            >
              <option value="">All Materials</option>
              {materials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          )}
          <DateRangeFilter onApply={handleDateRangeApply} />
        </div>

        {error && !modalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        {/* Raw Materials Table */}
        {viewTab === "raw_material" && (
          <>
            <div className="overflow-x-auto">
              {loading ? (
                <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
                  Loading...
                </div>
              ) : entries.length === 0 && !error ? (
                <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
                  No purchases in the selected period.
                </div>
              ) : entries.length === 0 ? null : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-t border-gray-200 dark:border-gray-800">
                      <SortableHeader label="Date" sortKey="date" sortConfig={rmSortConfig} onSort={rmRequestSort} />
                      <SortableHeader label="Material" sortKey="raw_material" sortConfig={rmSortConfig} onSort={rmRequestSort} />
                      <SortableHeader label="Qty" sortKey="qty" sortConfig={rmSortConfig} onSort={rmRequestSort} />
                      <SortableHeader label="Rate" sortKey="rate_per_unit" sortConfig={rmSortConfig} onSort={rmRequestSort} />
                      <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">Amount</TableCell>
                      <SortableHeader label="Supplier" sortKey="supplier" sortConfig={rmSortConfig} onSort={rmRequestSort} />
                      <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Notes</TableCell>
                      <TableCell isHeader className="relative px-3 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400"><span className="sr-only">Files</span></TableCell>
                      {!isViewer && (
                        <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">Actions</TableCell>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id} className="border-t border-gray-100 dark:border-gray-800">
                        <TableCell className="px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                          {new Date(entry.date).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {entry.raw_material_name}
                        </TableCell>
                        <TableCell className="px-5 py-3 text-sm text-right font-medium text-gray-800 dark:text-white/90">
                          {formatQty(entry.qty, entry.unit)} {displayUnit(entry.unit)}
                        </TableCell>
                        <TableCell className="px-5 py-3 text-sm text-right text-gray-600 dark:text-gray-400">
                          {entry.rate_per_unit != null ? `${formatRate(entry.rate_per_unit, entry.unit)}/${displayUnit(entry.unit)}` : "-"}
                        </TableCell>
                        <TableCell className="px-5 py-3 text-sm text-right font-medium text-gray-800 dark:text-white/90">
                          {entry.rate_per_unit != null
                            ? (entry.qty * entry.rate_per_unit).toLocaleString(undefined, { maximumFractionDigits: 2 })
                            : "-"}
                        </TableCell>
                        <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {entry.supplier_name || entry.supplier || "-"}
                        </TableCell>
                        <TableCell className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {entry.notes || "-"}
                        </TableCell>
                        <TableCell className="px-3 py-3 text-center">
                          <AttachmentViewer sourceType="raw_material_stock" sourceId={entry.id} count={entry.attachment_count ?? 0} />
                        </TableCell>
                        {!isViewer && (
                          <TableCell className="px-5 py-3 text-right">
                            <div className="flex items-center justify-end gap-3">
                              <button onClick={() => openEdit(entry)} className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400">Edit</button>
                              <button onClick={() => { setError(""); setPaidBillWarning(null); setDeleteTarget(entry); }} className="text-sm text-error-500 hover:text-error-600 dark:text-error-400">Delete</button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
            <Pagination total={total} limit={limit} offset={offset} onPrev={handlePrev} onNext={handleNext} />
          </>
        )}

        {/* Chick Purchases Table */}
        {viewTab === "chicks" && (
          <div className="overflow-x-auto">
            {chicksLoading ? (
              <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
                Loading...
              </div>
            ) : chickPurchases.length === 0 && !error ? (
              <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
                No chick purchases in the selected period.
              </div>
            ) : chickPurchases.length === 0 ? null : (
              <Table>
                <TableHeader>
                  <TableRow className="border-t border-gray-200 dark:border-gray-800">
                    <SortableHeader label="Date" sortKey="date" sortConfig={chickSortConfig} onSort={chickRequestSort} />
                    <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Batch</TableCell>
                    <SortableHeader label="Shed" sortKey="shed" sortConfig={chickSortConfig} onSort={chickRequestSort} />
                    <SortableHeader label="Birds" sortKey="bird_count" sortConfig={chickSortConfig} onSort={chickRequestSort} />
                    <SortableHeader label="Cost/Bird" sortKey="cost_per_bird" sortConfig={chickSortConfig} onSort={chickRequestSort} />
                    <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">Total</TableCell>
                    <SortableHeader label="Supplier" sortKey="supplier" sortConfig={chickSortConfig} onSort={chickRequestSort} />
                    <TableCell isHeader className="relative px-3 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400"><span className="sr-only">Files</span></TableCell>
                    {canWriteChicks && (
                      <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chickPurchases.map((cp) => (
                    <TableRow key={cp.id} className="border-t border-gray-100 dark:border-gray-800">
                      <TableCell className="px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                        {new Date(cp.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm font-medium text-gray-800 dark:text-white/90">
                        {cp.flock_name || "-"}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {cp.shed_name || "-"}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-right font-medium text-gray-800 dark:text-white/90">
                        {cp.bird_count.toLocaleString()}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-right text-gray-600 dark:text-gray-400">
                        {cp.cost_per_bird != null ? `Rs. ${cp.cost_per_bird.toFixed(2)}` : "-"}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-right font-medium text-gray-800 dark:text-white/90">
                        {cp.cost_per_bird != null
                          ? `Rs. ${(cp.cost_per_bird * cp.bird_count).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                          : "-"}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {cp.supplier_name || "-"}
                      </TableCell>
                      <TableCell className="px-3 py-3 text-center">
                        <AttachmentViewer sourceType="chick_purchase" sourceId={cp.id} count={cp.attachment_count ?? 0} />
                      </TableCell>
                      {canWriteChicks && (
                        <TableCell className="px-5 py-3 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <button onClick={() => openEditChick(cp)} className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400">Edit</button>
                            {canDeleteChicks && (
                              <button onClick={() => { setError(""); setPaidBillWarning(null); setDeleteChickTarget(cp); }} className="text-sm text-error-500 hover:text-error-600 dark:text-error-400">Delete</button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </div>

      {/* Log Purchase Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        className="max-w-md p-5 sm:p-8 mx-4"
      >
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editingChick ? "Edit Chick Purchase" : editing ? "Edit Purchase" : "Log Purchase"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        {unusualRateTypical !== null && modalOpen && (
          <label className="mb-4 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={confirmUnusualRate}
              onChange={(e) => setConfirmUnusualRate(e.target.checked)}
            />
            <span>
              Yes, {computedRate !== null ? `₹${computedRate.toFixed(2)}` : "this rate"} per {materialUnit === "ton" || materialUnit === "tonne" ? "kg" : materialUnit} is right — save it anyway.
            </span>
          </label>
        )}

        {duplicateInvoice && modalOpen && (
          <label className="mb-4 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={confirmDuplicateInvoice}
              onChange={(e) => setConfirmDuplicateInvoice(e.target.checked)}
            />
            <span>Yes, this is a second delivery on invoice {invoiceNumber.trim()} — save it anyway.</span>
          </label>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Purchase Type Toggle — only on create */}
          {!editing && !editingChick && canWriteChicks && (
            <div>
              <Label>Purchase Type</Label>
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="purchaseType"
                    value="raw_material"
                    checked={purchaseType === "raw_material"}
                    onChange={() => { setPurchaseType("raw_material"); setSupplier(""); setAttachments([]); setPendingAttachmentIds([]); }}
                    className="h-4 w-4 text-brand-500 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Raw Material</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="purchaseType"
                    value="chicks"
                    checked={purchaseType === "chicks"}
                    onChange={() => { setPurchaseType("chicks"); setSupplier(""); setAttachments([]); setPendingAttachmentIds([]); }}
                    className="h-4 w-4 text-brand-500 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Chicks</span>
                </label>
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="purchaseDate">Date *</Label>
            <Input
              id="purchaseDate"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          {/* Raw Material Fields */}
          {purchaseType === "raw_material" && (
            <>
              <div>
                <Label htmlFor="purchaseMaterial">Raw Material *</Label>
                <select
                  id="purchaseMaterial"
                  value={rawMaterialId}
                  onChange={(e) => { setRawMaterialId(e.target.value); setSupplier(""); }}
                  required
                  className={selectClass}
                >
                  <option value="">Select material</option>
                  {materials.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="purchaseQty">Quantity * <span className="text-xs text-gray-400">({materialUnit})</span></Label>
                <Input
                  id="purchaseQty"
                  type="number"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  placeholder={`e.g. ${materialUnit === "ton" || materialUnit === "tonne" ? "2" : "500"}`}
                  required
                  min={0.001}
                  step="0.001"
                />
              </div>

              <div>
                <Label htmlFor="purchaseTotalAmount">Total Amount</Label>
                <Input
                  id="purchaseTotalAmount"
                  type="number"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  placeholder="e.g. 12750"
                  min={0}
                  step="0.01"
                />
                {computedRate != null && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {/* computedRate is per KG (what is stored); formatRate scales it
                        to the display unit — 1 t for ₹28,000 read "₹28.00/ton". */}
                    Rate: ₹{formatRate(computedRate, materialUnit)}/{displayUnit(materialUnit)}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="purchaseInvoice">Invoice Number</Label>
                <Input
                  id="purchaseInvoice"
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="e.g. INV-001"
                />
              </div>
            </>
          )}

          {/* Chick Fields */}
          {purchaseType === "chicks" && (
            <>
              {editingChick ? (
                <div>
                  <Label>Flock / Shed</Label>
                  <input
                    type="text"
                    value={`${editingChick.flock_name || "-"}${editingChick.shed_name ? ` — ${editingChick.shed_name}` : ""}`}
                    readOnly
                    disabled
                    className={`${selectClass} cursor-not-allowed opacity-60`}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Sheds can't be changed on an existing purchase.
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <Label htmlFor="chickShed">Brooder Shed *</Label>
                    <select
                      id="chickShed"
                      value={shedId}
                      onChange={(e) => setShedId(e.target.value)}
                      required
                      className={selectClass}
                    >
                      <option value="">Select brooder shed</option>
                      {brooderSheds.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.shed_code}{s.capacity ? ` — Capacity: ${s.capacity.toLocaleString()}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="chickDestShed">Destination Layer Shed *</Label>
                    <select
                      id="chickDestShed"
                      value={destinationShedId}
                      onChange={(e) => setDestinationShedId(e.target.value)}
                      required
                      className={selectClass}
                    >
                      <option value="">Select layer shed</option>
                      {layerSheds.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.shed_code}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="chickCount">Bird Count *</Label>
                  <Input
                    id="chickCount"
                    type="number"
                    value={birdCount}
                    onChange={(e) => setBirdCount(e.target.value)}
                    placeholder="e.g. 5000"
                    required
                    min={1}
                  />
                </div>
                <div>
                  <Label htmlFor="chickCost">Cost per Bird (Rs.)</Label>
                  <Input
                    id="chickCost"
                    type="number"
                    value={costPerBird}
                    onChange={(e) => setCostPerBird(e.target.value)}
                    placeholder="e.g. 45.00"
                    min={0}
                    step="0.01"
                  />
                </div>
              </div>

              {costPerBird && birdCount && (
                <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
                  Total: Rs. {(parseFloat(costPerBird) * parseInt(birdCount || "0")).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </p>
              )}

              {!editingChick && (
                <div>
                  <Label htmlFor="chickBreed">Breed</Label>
                  <select
                    id="chickBreed"
                    value={chickBreedId}
                    onChange={(e) => setChickBreedId(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">Select breed</option>
                    {breeds.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {/* Supplier */}
          <div>
            <Label htmlFor="purchaseSupplier">Supplier *</Label>
            <select
              id="purchaseSupplier"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              required
              className={selectClass}
            >
              <option value="">Select supplier</option>
              {filteredSuppliers.map((s) => (
                <option key={s.id} value={s.id.toString()}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {purchaseType === "raw_material" && (
            <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Receipt check (optional)
              </p>
              <p className="mt-0.5 mb-3 text-xs text-gray-500 dark:text-gray-400">
                What arrived, against what was billed. The quantity above is the{" "}
                <strong>billed</strong> figure; what enters stock — and what this bill is worth — is
                what passes the check. Leave a box blank if it was not measured.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="weighbridgeQty">
                    Weighbridge{" "}
                    <span className="text-xs text-gray-400">({materialUnit || "kg"})</span>
                  </Label>
                  <Input
                    id="weighbridgeQty"
                    type="number"
                    value={weighbridgeQty}
                    onChange={(e) => setWeighbridgeQty(e.target.value)}
                    placeholder="—"
                    min={0}
                    step="any"
                  />
                </div>
                <div>
                  <Label htmlFor="moisturePct">
                    Moisture (%)
                    {preview?.moisture_std_pct != null && (
                      <span className="ml-1 text-xs text-gray-400">
                        standard {preview.moisture_std_pct}%
                      </span>
                    )}
                  </Label>
                  <Input
                    id="moisturePct"
                    type="number"
                    value={moisturePct}
                    onChange={(e) => setMoisturePct(e.target.value)}
                    placeholder="—"
                    min={0}
                    max={100}
                    step="any"
                  />
                </div>
                <div>
                  <Label htmlFor="aflatoxin">
                    Aflatoxin
                    {preview?.aflatoxin_limit != null && (
                      <span className="ml-1 text-xs text-gray-400">
                        limit {preview.aflatoxin_limit}
                      </span>
                    )}
                  </Label>
                  <Input
                    id="aflatoxin"
                    type="number"
                    value={aflatoxin}
                    onChange={(e) => setAflatoxin(e.target.value)}
                    placeholder="—"
                    min={0}
                    step="any"
                  />
                </div>
                <div>
                  <Label htmlFor="aflaOverride">Accept over the limit — why?</Label>
                  <Input
                    id="aflaOverride"
                    type="text"
                    value={aflaOverride}
                    onChange={(e) => setAflaOverride(e.target.value)}
                    placeholder="Required to accept a failed load"
                  />
                </div>
              </div>

              {/* The number that decides what the farm pays, shown BEFORE the
                  operator commits. Computed by the server through the same
                  calculation the save uses, so what is previewed is what is
                  stored. */}
              {previewError && (
                <p className="mt-3 rounded-lg bg-error-50 px-3 py-2 text-xs text-error-600 dark:bg-error-500/10 dark:text-error-400">
                  {previewError}
                </p>
              )}
              {preview?.checked && (
                <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs dark:bg-white/[0.04]">
                  <p className="font-medium text-gray-800 dark:text-white/90">
                    Accepted {preview.accepted_qty.toLocaleString("en-IN")} of{" "}
                    {preview.bill_qty.toLocaleString("en-IN")}
                    {preview.deduction_amount != null && preview.deduction_amount > 0 && (
                      <>
                        {" "}
                        · {reportInr(preview.deduction_amount)} off this bill
                      </>
                    )}
                  </p>
                  <p className="mt-0.5 text-gray-500 dark:text-gray-400">
                    {preview.shortage_qty > 0
                      ? `Short ${preview.shortage_qty.toLocaleString("en-IN")}`
                      : "No shortage beyond tolerance"}
                    {preview.moisture_qty > 0 &&
                      ` · moisture ${preview.moisture_qty.toLocaleString("en-IN")}`}
                  </p>
                  {preview.aflatoxin_failed && (
                    <p className="mt-1 font-medium text-error-600 dark:text-error-400">
                      Aflatoxin is over the limit — reject the load, or say why it is being
                      accepted. It is never deducted.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notes — common */}
          <div>
            <Label htmlFor="purchaseNotes">Notes</Label>
            <textarea
              id="purchaseNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          {/* Receipts / Invoices */}
          {!editingChick && (
            <AttachmentUpload
              attachments={attachments}
              onUpload={(a) => {
                setAttachments((prev) => [...prev, a]);
                setPendingAttachmentIds((prev) => [...prev, a.id]);
              }}
              onDelete={(id) => {
                setAttachments((prev) => prev.filter((a) => a.id !== id));
                setPendingAttachmentIds((prev) => prev.filter((aid) => aid !== id));
              }}
              disabled={saving}
            />
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={closeModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={saving || (purchaseType === "raw_material" ? !isRawMaterialFormValid : !isChickFormValid)}
            >
              {saving ? "Saving..." : editing || editingChick ? "Save Changes" : "Log Purchase"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Purchase"
        message={
          <>
            Are you sure you want to delete this purchase entry? This action cannot be undone.
            {paidBillWarning && (
              <div className="mt-4 rounded-lg bg-warning-50 p-3 text-sm text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
                {paidBillWarning}
              </div>
            )}
            {error && deleteTarget && (
              <div className="mt-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
                {error}
              </div>
            )}
          </>
        }
        confirmLabel={paidBillWarning ? "Delete anyway" : "Delete"}
        busy={deleting}
        busyLabel="Deleting…"
        onConfirm={handleDelete}
        onClose={() => { setDeleteTarget(null); setPaidBillWarning(null); }}
      />

      {/* Delete Chick Purchase Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteChickTarget}
        title="Delete Chick Purchase"
        message={
          <>
            Are you sure you want to delete this chick purchase? This will remove the
            birds it added to the flock. This action cannot be undone.
            {paidBillWarning && (
              <div className="mt-4 rounded-lg bg-warning-50 p-3 text-sm text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
                {paidBillWarning}
              </div>
            )}
            {error && deleteChickTarget && (
              <div className="mt-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
                {error}
              </div>
            )}
          </>
        }
        busy={deleting}
        busyLabel="Deleting…"
        confirmLabel={paidBillWarning ? "Delete anyway" : "Delete"}
        onConfirm={handleDeleteChick}
        onClose={() => { setDeleteChickTarget(null); setPaidBillWarning(null); }}
      />
    </>
  );
}
